import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { ChevronLeft, ChevronRight, Package, X, CheckCircle2, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api from '../utils/api';

const CalendarView = ({ purchasedOptions = [], onTaskUpdate }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [daysInMonth, setDaysInMonth] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDayTasks, setSelectedDayTasks] = useState([]);
  const [updatingTaskId, setUpdatingTaskId] = useState(null);

  useEffect(() => {
    generateCalendar();
  }, [currentDate, purchasedOptions]);

  useEffect(() => {
    if (selectedDay) {
      const tasks = purchasedOptions.filter(opt => opt.date_debut === selectedDay.dateStr);
      setSelectedDayTasks(tasks);
    }
  }, [selectedDay, purchasedOptions]);

  const generateCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysCount = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Jours vides avant le premier jour du mois
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push({ date: null, options: [] });
    }

    // Jours du mois
    for (let day = 1; day <= daysCount; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const optionsForDay = purchasedOptions.filter(opt => opt.date_debut === dateStr);
      days.push({ date: day, dateStr, options: optionsForDay, month, year });
    }

    setDaysInMonth(days);
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDayClick = (day) => {
    if (day.date) {
      setSelectedDay(day);
    }
  };

  const closePopup = () => {
    setSelectedDay(null);
    setSelectedDayTasks([]);
  };

  const goToPreviousDay = () => {
    if (!selectedDay) return;
    
    const currentDayDate = new Date(selectedDay.year, selectedDay.month, selectedDay.date);
    currentDayDate.setDate(currentDayDate.getDate() - 1);
    
    const newYear = currentDayDate.getFullYear();
    const newMonth = currentDayDate.getMonth();
    const newDate = currentDayDate.getDate();
    const newDateStr = `${newYear}-${String(newMonth + 1).padStart(2, '0')}-${String(newDate).padStart(2, '0')}`;
    
    // Si on change de mois, mettre à jour le calendrier
    if (newMonth !== currentDate.getMonth() || newYear !== currentDate.getFullYear()) {
      setCurrentDate(new Date(newYear, newMonth, 1));
    }
    
    const optionsForDay = purchasedOptions.filter(opt => opt.date_debut === newDateStr);
    setSelectedDay({ date: newDate, dateStr: newDateStr, options: optionsForDay, month: newMonth, year: newYear });
  };

  const goToNextDay = () => {
    if (!selectedDay) return;
    
    const currentDayDate = new Date(selectedDay.year, selectedDay.month, selectedDay.date);
    currentDayDate.setDate(currentDayDate.getDate() + 1);
    
    const newYear = currentDayDate.getFullYear();
    const newMonth = currentDayDate.getMonth();
    const newDate = currentDayDate.getDate();
    const newDateStr = `${newYear}-${String(newMonth + 1).padStart(2, '0')}-${String(newDate).padStart(2, '0')}`;
    
    // Si on change de mois, mettre à jour le calendrier
    if (newMonth !== currentDate.getMonth() || newYear !== currentDate.getFullYear()) {
      setCurrentDate(new Date(newYear, newMonth, 1));
    }
    
    const optionsForDay = purchasedOptions.filter(opt => opt.date_debut === newDateStr);
    setSelectedDay({ date: newDate, dateStr: newDateStr, options: optionsForDay, month: newMonth, year: newYear });
  };

  const handleToggleTask = async (taskId, currentStatus) => {
    setUpdatingTaskId(taskId);
    try {
      if (currentStatus) {
        await api.put(`/dashboard/todos/${taskId}/uncomplete`);
      } else {
        await api.put(`/dashboard/todos/${taskId}/complete`);
      }
      
      // Mettre à jour localement
      setSelectedDayTasks(prev => prev.map(task => 
        task.id === taskId ? { ...task, task_completed: !currentStatus } : task
      ));
      
      // Notifier le parent pour mettre à jour la liste globale
      if (onTaskUpdate) {
        onTaskUpdate(taskId, !currentStatus);
      }
      
      toast.success(currentStatus ? 'Tâche décochée' : 'Tâche terminée !');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdatingTaskId(null);
    }
  };

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('fr-FR', options);
  };

  return (
    <>
      <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h3 className="text-lg sm:text-xl font-medium text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>
          <div className="flex space-x-1 sm:space-x-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={previousMonth}
              data-testid="calendar-prev-btn"
              className="text-slate-600 hover:bg-slate-100 p-1 sm:p-2"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={nextMonth}
              data-testid="calendar-next-btn"
              className="text-slate-600 hover:bg-slate-100 p-1 sm:p-2"
            >
              <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {dayNames.map((day, index) => (
            <div key={index} className="text-center text-[10px] sm:text-xs font-medium text-slate-500 pb-1 sm:pb-2">
              {day}
            </div>
          ))}

          {daysInMonth.map((day, index) => (
            <div
              key={index}
              onClick={() => handleDayClick(day)}
              className={`aspect-square border rounded-lg p-0.5 sm:p-1 transition-all ${
                day.date 
                  ? 'border-slate-200 hover:bg-slate-50 cursor-pointer hover:border-rose-300 hover:shadow-sm' 
                  : 'border-transparent'
              } ${day.options.length > 0 ? 'bg-rose-50 border-rose-200' : ''}`}
              data-testid={day.date ? `calendar-day-${day.date}` : undefined}
            >
              {day.date && (
                <>
                  <div className="text-[10px] sm:text-xs font-medium text-slate-900 text-center mb-0.5 sm:mb-1">{day.date}</div>
                  {day.options.length > 0 && (
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-4 sm:w-5 sm:h-5 bg-rose-600 rounded-full flex items-center justify-center">
                        <span className="text-[8px] sm:text-[10px] text-white font-bold">{day.options.length}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {purchasedOptions.length === 0 && (
          <div className="text-center py-4 sm:py-6 mt-2 sm:mt-4" data-testid="calendar-empty-state">
            <Package className="w-6 h-6 sm:w-8 sm:h-8 text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
            <p className="text-xs sm:text-sm text-slate-500">Aucune option pour ce mois</p>
          </div>
        )}
      </Card>

      {/* Popup du jour sélectionné */}
      {selectedDay && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closePopup}
          data-testid="calendar-day-popup-overlay"
        >
          <div 
            className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="calendar-day-popup"
          >
            {/* Bouton fermer */}
            <Button
              size="sm"
              variant="ghost"
              onClick={closePopup}
              data-testid="popup-close-btn"
              className="absolute top-2 right-2 text-slate-400 hover:text-slate-600 hover:bg-rose-100 p-1 rounded-full z-10"
            >
              <X className="w-5 h-5" strokeWidth={2} />
            </Button>
            
            {/* Header du popup */}
            <div className="bg-rose-50 border-b border-rose-100 p-4 pt-3">
              <div className="flex items-center justify-between">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={goToPreviousDay}
                  data-testid="popup-prev-day-btn"
                  className="text-slate-600 hover:bg-rose-100 p-2"
                >
                  <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
                </Button>
                
                <div className="text-center flex-1 px-8">
                  <h3 className="text-lg font-semibold text-slate-900 capitalize" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {formatDate(selectedDay.dateStr)}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {selectedDayTasks.length} tâche{selectedDayTasks.length !== 1 ? 's' : ''} à réaliser
                  </p>
                </div>
                
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={goToNextDay}
                  data-testid="popup-next-day-btn"
                  className="text-slate-600 hover:bg-rose-100 p-2"
                >
                  <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
                </Button>
              </div>
            </div>

            {/* Contenu du popup - Liste des tâches */}
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {selectedDayTasks.length === 0 ? (
                <div className="text-center py-8" data-testid="popup-empty-state">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
                  <p className="text-slate-500">Aucune tâche pour ce jour</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="popup-tasks-list">
                  {selectedDayTasks.map((task) => (
                    <div 
                      key={task.id}
                      className={`p-3 border rounded-lg transition-all ${
                        task.task_completed 
                          ? 'bg-slate-50 border-slate-200 opacity-70' 
                          : 'border-slate-200 hover:border-rose-200 hover:bg-rose-50/30'
                      }`}
                      data-testid={`popup-task-${task.id}`}
                    >
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          checked={task.task_completed}
                          disabled={updatingTaskId === task.id}
                          onCheckedChange={() => handleToggleTask(task.id, task.task_completed)}
                          data-testid={`popup-task-checkbox-${task.id}`}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium text-sm ${task.task_completed ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                            {task.option_name}
                          </p>
                          <p className={`text-xs mt-0.5 ${task.task_completed ? 'text-slate-400' : 'text-slate-600'}`}>
                            {task.property_name}
                          </p>
                          <p className={`text-xs ${task.task_completed ? 'text-slate-400' : 'text-slate-500'}`}>
                            {task.nom_voyageur}
                          </p>
                          
                          {/* Réponse du voyageur si présente */}
                          {task.question_answer && (
                            <div className={`mt-2 p-2 rounded-lg text-xs ${task.task_completed ? 'bg-slate-100' : 'bg-blue-50 border border-blue-200'}`}>
                              <p className={`font-medium flex items-center gap-1 ${task.task_completed ? 'text-slate-400' : 'text-blue-700'}`}>
                                <MessageSquare className="w-3 h-3" />
                                Réponse :
                              </p>
                              <p className={`mt-1 ${task.task_completed ? 'text-slate-400' : 'text-blue-900'}`}>
                                "{task.question_answer}"
                              </p>
                            </div>
                          )}
                          
                          {task.task_completed && (
                            <p className="text-xs text-green-600 mt-1 flex items-center">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Terminée
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CalendarView;
